import * as angular from 'angular';

export enum ModalType {
  Custom = 'Custom',
  Default = 'Default',
  Draggable = 'Draggable',
  Full = 'Full',
  Legacy = 'Legacy',
}

export enum ModalTheme {
  Danger = 'Danger',
  DangerLight = 'DangerLight',
  Default = 'Default',
  Info = 'Info',
  Success = 'Success',
  Warning = 'Warning',
}

// eslint-disable-next-line @typescript-eslint/no-namespace
namespace Compat {
  export type TClass = new (...a: any[]) => any;
  export function wrapCtrl<T extends TClass>(Class: T): (...a: ConstructorParameters<T>) => InstanceType<T> {
    let scopeIndex = -1;

    function ES5Class(this: InstanceType<T>, ...args: ConstructorParameters<T>): InstanceType<T> | null {
      // Reflect.construct "Target" must be instantiable,
      // even though the __() constructor is not called
      function __() {}
      __.prototype = Object.create(this);

      let inst: InstanceType<T> | null = Reflect.construct(Class, args, __);
      // Ensure Controller#$onDestroy is called, UIB doesn't do this for us :/
      // see: https://github.com/angular-ui/bootstrap/blob/1.2.5/src/modal/modal.js#L694-L696
      if (inst && typeof inst.$onDestroy === 'function') {
        if (typeof args[scopeIndex]?.$on === 'function') {
          args[scopeIndex].$on('$destroy', () => {
            inst?.$onDestroy();
            inst = null;
            // @ts-ignore
            args = null;
          });
        }
      }

      return inst;
    }

    const statics = Object.getOwnPropertyDescriptors(Class);
    const $inject = Class.$inject ?? [];
    if (Array.isArray($inject)) {
      const value = $inject.concat('$scope');
      scopeIndex = $inject.length;
      Object.assign(statics, {
        $inject: {
          value,
        },
      });
    }

    // @ts-ignore
    return Object.defineProperties(ES5Class, {
      ...statics,
      $$ngIsClass: {
        value: false,
      },
    });
  }
}

/**
 * @deprecated Prefer the `DialogService`
 *
 * Singleton service for invoking standard popup modals
 */
export class ModalService {
  static $inject = ['$uibModal', '$rootScope'];

  public constructor(
    private $uibModal: any,
    private $rootScope: angular.IRootScopeService
  ) {}

  private _open(opts: Record<string, any>): any {
    let controller = opts['controller'];
    if (typeof controller === 'function') {
      /**
       * `bindToController` doesn't work properly with es6 classes..
       * an instance of the class is constructed without the scope
       * members being copied over and wreaks all sorts of havoc.
       * Controller bindings the view expects to be defined are
       * unbound
       *
       * Additionally, UIB does not invoke the modal controller's
       * $onDestroy lifecycle method.. only $onInit.. so we patch
       * this as well
       */
      controller = Compat.wrapCtrl(controller);
      opts = {
        ...opts,
        controller,
      };
    }
    return this.$uibModal.open(opts);
  }

  open(attributes: any, options: any, isDraggable = false) {
    const scope = this.$rootScope.$new();
    angular.extend(scope, attributes);
    let opts;
    let windowClass = isDraggable ? 'ds-modal calc-editor-window draggable-modal ' : 'mode-modal ';
    windowClass += options.windowClass ?? '';

    if (isDraggable) {
      opts = angular.extend(options || {}, {
        scope,
        windowClass,
        animation: false,
        backdrop: false,
      });
    } else {
      opts = angular.extend(options || {}, {
        animation: true,
        windowClass,
        scope,
      });
    }

    const modal = this._open(opts);
    modal.result.catch(function () {});

    return modal;
  }

  openFullModal(attributes: any, options: any) {
    const scope = this.$rootScope.$new();
    angular.extend(scope, attributes);
    const opts = angular.extend(options || {}, {
      animation: false,
      backdropClass: 'mode-full-backdrop',
      keyboard: false,
      windowClass: 'mode-full-modal mode-modal',
      scope: scope,
    });

    const modal = this._open(opts);
    modal.result.catch(function () {});

    return modal;
  }

  openLargeModal(attributes: any, options = {}) {
    const scope = this.$rootScope.$new();
    angular.extend(scope, attributes);

    const opts = angular.extend(options, {
      animation: false,
      keyboard: true,
      windowClass: 'mode-large-modal ds-modal',
      scope: scope,
    });

    const modal = this._open(opts);
    modal.result.catch(function () {});

    return modal;
  }

  openCustom(attributes: any, options: any) {
    const scope = this.$rootScope.$new();
    angular.extend(scope, attributes);
    const opts = angular.extend(options || {}, {
      windowClass: 'mode-custom-modal',
      backdrop: false,
      scope: scope,
    });

    const modal = this._open(opts);
    modal.result.catch(function () {});

    return modal;
  }

  openLegacy(attributes: any, options: any) {
    const scope = this.$rootScope.$new();
    angular.extend(scope, attributes);
    const opts = angular.extend(options || {}, {
      animation: true,
      windowClass: 'mode-modal-window splash-open',
      scope: scope,
    });

    const modal = this._open(opts);
    modal.result.catch(function () {});

    return modal;
  }

  openDraggable(attributes: any, options: any) {
    const scope = this.$rootScope.$new();
    angular.extend(scope, attributes);

    let windowClass = 'mode-modal draggable-modal';
    if (options.windowClass != null) {
      windowClass = `${windowClass} ${options.windowClass}`;
    }

    const uibOptions = angular.extend(options || {}, {
      scope,
      windowClass,
      animation: true,
      backdrop: false,
    });

    const modal = this._open(uibOptions);
    modal.result.catch(function () {});

    return modal;
  }

  // Design System modal styles are attached to this new modal type
  openDsDraggable(attributes: any, options: any) {
    const scope = this.$rootScope.$new();
    angular.extend(scope, attributes);

    let windowClass = 'ds-modal draggable-modal';
    if (options.windowClass != null) {
      windowClass = `${windowClass} ${options.windowClass}`;
    }

    const uibOptions = angular.extend(options || {}, {
      scope,
      windowClass,
      animation: false,
      backdrop: false,
    });

    const modal = this._open(uibOptions);
    modal.result.catch(function () {});

    return modal;
  }
}
